import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { Alert, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Afr, getSelectedAfr, setToImportedAfr, Table } from '../../redux/AfrSlice';
import {
    AFRDocumentStarter, AfrImportSender, DocumentReference, FileSender, getAfrInstance, getTaxonomy,
    getTemplate, importAfrInstanceFile, importFinancialReportSpreadsheetFile, sendFile
} from '../../redux/DocumentsSlice';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { getWorkflowState, isDirty, setIsDirty, setWorkflowStatus, Workflow } from '../../redux/MiscSlice';
import { GenericActionTile } from '../core/GenericActionTile';
import { GenericActionTileUploadFile } from '../core/GenericActionTileUploadFile';
import { ListItem } from '../../redux/VisualizationSlice';
import { defaultReportFormatValue, IReportFormat } from '../feature/SystemReports';
import Swal from 'sweetalert2';
import utilityHelper from '../../utils/utilityHelper';
import { TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';


export const downloadFormats: Array<IReportFormat> = [
    { label: 'Excel', value: 'EXCEL' },
    { label: 'PDF', value: 'PDF' },
    { label: 'XBRL', value: 'XBRL' }];



const ReportDataImporter = (props:
    {
        userRole: string,
        taxonomyType: TaxonomyTypeEnum | null,
        importLabel: string,
        downloadTemplateLabel: string
    }) => {

    const dispatch = useAppDispatch();

    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const afrYear = currentAfr ? currentAfr.year : 0;
    const logicalPeriod = currentAfr ? currentAfr.logicalPeriod : "ANNUAL";
    const entity: Entity | null = useAppSelector(getSelectedEntity);

    // Hooks
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
    const [file, setFile] = React.useState<File | null>(null);
    const isDirtyInfo: boolean = useAppSelector(isDirty);

    const [isUploadingInstance, setIsUploadingInstance] = React.useState<boolean>(false);
    const [isUploadingSpreadsheet, setIsUploadingSpreadsheet] = React.useState<boolean>(false);
    const [isDownloadingAfrTemplate, setIsDownloadingAfrTemplate] = React.useState<boolean>(false);
    var reportName = "";

    const importExportActionUploadedFile = function (atype: string, ename: string, ecode: string, yr: number, uploadedFile: File) {
        setFile(uploadedFile);
        switch (atype) {
            case "UPLOAD_SPREADSHEET":
            case "UPLOAD_INSTANCE":
                importAfr(atype, uploadedFile);
                break;
        }

    }
    async function downloadDocumentTemplateToBrowser(doc: DocumentReference) {
        if (doc != null) {
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            await fetch(url)
                .then(res => res.blob())
                .then(newBlob => {
                    const data = window.URL.createObjectURL(newBlob);
                    var link = document.createElement("a");
                    link.href = data;
                    //                    console.log(data);
                    link.download = doc.uri; //"TemplateDocument.xlsm";
                    link.click();
                    setTimeout(function () {
                        window.URL.revokeObjectURL(data);
                    })
                });
        }
    }


    function getAfrData(docType: string, bPopulate: 'NO' | 'CURRENT_YEAR' | 'PRIOR_YEAR') {
        if (!currentAfr || !entity) {
            return null;
        }
        const afrData: AFRDocumentStarter = {
            afrYear: currentAfr.year,
            entityCode: currentAfr.afrId,
            fileType: docType,
            outputType: null,
            populate: bPopulate
        }

        return afrData;
    }

    async function uploadDocument(docType: string) {
        const uploadedFile = file;
        if (uploadedFile !== null &&
            docType !== null) {
            let fileSender: FileSender = { file: uploadedFile, fileType: docType };
            const response = await dispatch(sendFile(fileSender));
            const doc = unwrapResult(response);
        }
    }

    async function downloadDocumentTemplate(docType: string, bPopulate: 'NO' | 'CURRENT_YEAR' | 'PRIOR_YEAR') {
        var request = getAfrData(docType, bPopulate);
        if (!request) {
            alert("Must have a selected entity and AFR!");
            return;
        }
        const response = await dispatch(getTemplate(request));
        const doc = unwrapResult(response);
        if (doc !== null) {
            await downloadDocumentTemplateToBrowser(doc);
        }
    }

    const importExportAction = async function (atype: string, ename: string, ecode: string, yr: number) {

        switch (atype) {
            case "DOWNLOAD_TEMPLATE":
                setIsDownloadingAfrTemplate(true);
                await downloadDocumentTemplate(atype, 'NO');
                setIsDownloadingAfrTemplate(false);
                break;

        }
    }

    async function importAfr(docType: string, uploadedFile: File) {

        if (entity && currentAfr && uploadedFile !== null &&
            docType !== null) {
            // NOTE - we are overloading the 'entityCode' attribute to actually carry the afrid, which includes the taxonomy type and logical period
            let fileSender: AfrImportSender = { entityCode: currentAfr.afrId, afrYear: currentAfr.year, file: uploadedFile, fileType: docType, sections: [] };
            var response = null;
            switch (docType) {
                case "UPLOAD_SPREADSHEET":
                    setIsUploadingSpreadsheet(true);
                    //if there are any section qualifiers - add them here, they only apply to spreadsheet uploads
                    fileSender.sections = [];
                    response = await dispatch(importFinancialReportSpreadsheetFile(fileSender));
                    setIsUploadingSpreadsheet(false);
                    break;
                case "UPLOAD_INSTANCE":
                    setIsUploadingInstance(true);
                    response = await dispatch(importAfrInstanceFile(fileSender));
                    setIsUploadingInstance(false);
                    break;

               default:
                    alert("Invalid choice: " + docType);
                    return;
            }
            const importafr = unwrapResult(response);
            if (importafr != null) {
                await dispatch(setToImportedAfr(importafr.afr));

                Swal.fire(utilityHelper.getSuccessfulUploadJSON(importafr.afr));

                var doc = importafr.report;
                if (doc != null) {
                    //TODO make this a generic function in document slice
                    //that takes mime type, base64 encoded string, filename
                    //and returns a blob.
                    //TODO expand to handle xhtml and excel files
                    var url = "data:" + doc.mimeType + ";base64," + doc.content;
                    fetch(url)
                        .then(res => res.blob())
                        .then(otho => {
                            const data = window.URL.createObjectURL(otho);
                            var link = document.createElement("a");
                            link.href = data;
                            link.download = doc.uri;
                            link.click();
                            setTimeout(function () {
                                // For Firefox it is necessary to delay revoking the ObjectURL
                                window.URL.revokeObjectURL(data);
                            }, 100);
                        });

                }
                //turn on the dirty flag
                dispatch(setIsDirty({ IsDirty: true, Page: "" }))
            } else {
                Swal.fire('Oops...', 'Something went wrong!', 'error')
            }

        }
    }



    return (
        <React.Fragment>
            {entity !== null ?
                <Row>
                    <Col>
                        <GenericActionTile
                            actionType="DOWNLOAD_TEMPLATE"
                            clickAction={importExportAction}
                            entityName={entity.name}
                            entityCode={entity.code}
                            afrYear={afrYear}
                            tileTitle="Data Template"
                            tileDescription={props.downloadTemplateLabel}
                            icon="bi bi-download"
                            isEnabled={true}
                            isLoading={isDownloadingAfrTemplate}
                        />
                        <GenericActionTileUploadFile
                            actionType="UPLOAD_SPREADSHEET"
                            clickAction={importExportActionUploadedFile}
                            entityName={entity.name}
                            entityCode={entity.code}
                            afrYear={afrYear}
                            tileTitle="Upload Court Reporting 939.185 Data"
                            tileDescription={props.importLabel}
                            icon="bi bi-arrow-bar-up"
                            isEnabled={(!isDirtyInfo )
                                && (!(adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin))}
                            uploadedFile={file}
                            isLoading={isUploadingSpreadsheet}
                        />
                    </Col>
                </Row>
                : <div>No entity selected</div>}
        </React.Fragment>
    );
}

export default ReportDataImporter;